import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/FirebaseAuthContext';
import { authAPI, uploadAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import PaymentSettings from './PaymentSettings';
import {
  User,
  Lock,
  Eye,
  EyeOff,
  Camera,
  Save,
  Mail,
  Phone,
  MapPin,
  FileText,
  DollarSign,
  Edit2,
  Globe,
} from 'lucide-react';

// Read-only row used in the profile "view" mode
const InfoRow = ({ label, value, sub }) => (
  <div style={{ padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
    <div style={{ fontSize: '12px', color: 'var(--textSecondary)', marginBottom: '2px' }}>{label}</div>
    <div style={{ fontSize: '15px', fontWeight: 500, wordBreak: 'break-word' }}>{value || '—'}</div>
    {sub && <div style={{ fontSize: '12px', color: 'var(--textSecondary)', marginTop: '2px' }}>{sub}</div>}
  </div>
);

const Settings = () => {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();

  // Profile edit state
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    username: user?.username || '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    bio: user?.bio || '',
    emailPublic: user?.emailPublic || false,
    realNamePublic: user?.realNamePublic || false,
    locationPublic: user?.locationPublic || false,
    location: {
      street: user?.location?.street || '',
      city: user?.location?.city || '',
      state: user?.location?.state || '',
      zipCode: user?.location?.zipCode || '',
    },
  });

  // Password change state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Show password toggles
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Profile image upload
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(user?.profileImage || '');

  // UI state
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Sync user data when user changes
  useEffect(() => {
    if (user) {
      setProfileData({
        username: user.username || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        phone: user.phone || '',
        bio: user.bio || '',
        emailPublic: user.emailPublic || false,
        realNamePublic: user.realNamePublic || false,
        locationPublic: user.locationPublic || false,
        location: {
          street: user.location?.street || '',
          city: user.location?.city || '',
          state: user.location?.state || '',
          zipCode: user.location?.zipCode || '',
        },
      });
      setProfileImagePreview(user.profileImage || '');
    }
  }, [user]);

  const handleProfileImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImage(file);
      setProfileImagePreview(URL.createObjectURL(file));
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      let imageUrl = profileImagePreview;

      // Upload image if new one selected
      if (profileImage) {
        const uploadResponse = await uploadAPI.uploadProfile(profileImage);
        imageUrl = uploadResponse.data.data.url;
      }

      // Update profile
      const response = await authAPI.updateDetails({
        ...profileData,
        profileImage: imageUrl,
      });

      updateUser(response.data.data);
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      setProfileImage(null);
      setEditingProfile(false);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to update profile',
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match' });
      setLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Password must be at least 6 characters' });
      setLoading(false);
      return;
    }

    try {
      await authAPI.updatePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });

      setMessage({ type: 'success', text: 'Password updated successfully!' });
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });

      // Log out and redirect to login
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 2000);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to update password',
      });
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'password', label: 'Password', icon: Lock },
    { id: 'payment', label: 'Payment', icon: DollarSign },
  ];

  return (
    <div style={{
      maxWidth: '900px',
      margin: '0 auto',
      padding: isMobile ? '16px' : '32px',
      minHeight: 'calc(100vh - 120px)',
    }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: isMobile ? '24px' : '32px', marginBottom: '8px' }}>
          Settings
        </h1>
        <p style={{ color: 'var(--textSecondary)' }}>
          Manage your account settings and preferences
        </p>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        borderBottom: '1px solid var(--border)',
        marginBottom: '32px',
        overflowX: 'auto',
      }}>
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setMessage({ type: '', text: '' });
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: isMobile ? '12px 16px' : '12px 24px',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === tab.id ? '2px solid var(--primary)' : '2px solid transparent',
                color: activeTab === tab.id ? 'var(--primary)' : 'var(--textSecondary)',
                cursor: 'pointer',
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: activeTab === tab.id ? '600' : '500',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap',
              }}
            >
              <Icon size={isMobile ? 18 : 20} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Message */}
      {message.text && (
        <div style={{
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '24px',
          backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
          color: message.type === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${message.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
        }}>
          {message.text}
        </div>
      )}

      {/* Profile Tab — view mode */}
      {activeTab === 'profile' && !editingProfile && (
        <div style={{
          background: 'var(--cardBackground)',
          padding: isMobile ? '20px' : '32px',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
              <img
                src={profileImagePreview || `https://ui-avatars.com/api/?name=${user?.username}&background=e60023&color=fff&size=64`}
                alt="Profile"
                style={{ width: '56px', height: '56px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
              />
              <h3 style={{ margin: 0, fontSize: '20px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                @{user?.username}
              </h3>
            </div>
            <button
              type="button"
              onClick={() => { setEditingProfile(true); setMessage({ type: '', text: '' }); }}
              className="btn btn-outline-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', whiteSpace: 'nowrap' }}
            >
              <Edit2 size={16} /> Edit
            </button>
          </div>
          <InfoRow label="Name" value={`${user?.firstName || ''} ${user?.lastName || ''}`.trim()} sub={user?.realNamePublic ? 'Visible on your public profile' : 'Hidden from your public profile'} />
          <InfoRow label="Email" value={user?.email} sub={user?.emailPublic ? 'Visible on your public profile' : 'Hidden from your public profile'} />
          <InfoRow label="Phone" value={user?.phone} />
          <InfoRow label="Shipping address" value={[user?.location?.street, user?.location?.city, user?.location?.state, user?.location?.zipCode].filter(Boolean).join(', ')} />
          <InfoRow label="Bio" value={user?.bio} />
        </div>
      )}

      {/* Profile Tab — edit mode */}
      {activeTab === 'profile' && editingProfile && (
        <form onSubmit={handleProfileUpdate}>
          <div style={{
            background: 'var(--cardBackground)',
            padding: isMobile ? '20px' : '32px',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}>
            {/* Profile Image Upload */}
            <div style={{ marginBottom: '32px', textAlign: 'center' }}>
              <div style={{
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                margin: '0 auto',
                position: 'relative',
                overflow: 'hidden',
                border: '4px solid var(--border)',
              }}>
                <img
                  src={profileImagePreview || `https://ui-avatars.com/api/?name=${user?.username}&background=e60023&color=fff&size=120`}
                  alt="Profile"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
                <label
                  htmlFor="profile-image-upload"
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    padding: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Camera size={20} />
                </label>
                <input
                  id="profile-image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleProfileImageSelect}
                  style={{ display: 'none' }}
                />
              </div>
              <p style={{
                marginTop: '12px',
                fontSize: '14px',
                color: 'var(--textSecondary)',
              }}>
                Click camera icon to change profile picture
              </p>
            </div>

            {/* Form Fields */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: '20px',
            }}>
              {/* Seller requirements note */}
              <div style={{
                gridColumn: isMobile ? '1' : '1 / -1',
                padding: '12px 14px',
                background: '#FEF3C7',
                border: '1px solid #F59E0B',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#7c5e10',
                lineHeight: 1.5,
              }}>
                <strong>🚚 Required to sell &amp; ship:</strong> Your <strong>first name</strong>,{' '}
                <strong>phone number</strong>, <strong>email</strong>, and full{' '}
                <strong>shipping address</strong> must be filled in below. Carriers (USPS) require
                the seller's phone and email on every shipping label, and your address is the
                "ship from" location used to calculate rates. Without these, buyers won't be able
                to purchase your items with shipping.
              </div>

              {/* Username */}
              <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', fontWeight: '500', fontSize: '14px' }}>
                  <User size={16} />
                  Username
                </label>
                <input
                  type="text"
                  className="input"
                  value={profileData.username}
                  onChange={(e) => setProfileData({ ...profileData, username: e.target.value })}
                  placeholder="username"
                />
              </div>

              {/* Privacy toggles */}
              <div style={{ gridColumn: isMobile ? '1' : '1 / -1', background: 'var(--background)', borderRadius: '8px', padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600, fontSize: '14px', marginBottom: '10px' }}>
                  <Globe size={16} /> Public profile privacy
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: '8px' }}>
                  <input
                    type="checkbox"
                    checked={profileData.emailPublic}
                    onChange={(e) => setProfileData({ ...profileData, emailPublic: e.target.checked })}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <span style={{ fontSize: '14px' }}>Show my email on my public profile</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: '8px' }}>
                  <input
                    type="checkbox"
                    checked={profileData.realNamePublic}
                    onChange={(e) => setProfileData({ ...profileData, realNamePublic: e.target.checked })}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <span style={{ fontSize: '14px' }}>Show my real name on my public profile</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={profileData.locationPublic}
                    onChange={(e) => setProfileData({ ...profileData, locationPublic: e.target.checked })}
                    style={{ width: '18px', height: '18px' }}
                  />
                  <span style={{ fontSize: '14px' }}>Show my general location (city, state) on my public profile</span>
                </label>
                <p style={{ fontSize: '12px', color: 'var(--textSecondary)', margin: '10px 0 0' }}>
                  Your username is always public. Email, real name, and location are hidden from other users unless you turn these on. Your street address and ZIP are never shown publicly.
                </p>
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                  fontSize: '14px',
                }}>
                  First Name
                </label>
                <input
                  type="text"
                  value={profileData.firstName}
                  onChange={(e) => setProfileData({ ...profileData, firstName: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid var(--border)',
                    fontSize: '14px',
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: '500',
                  fontSize: '14px',
                }}>
                  Last Name
                </label>
                <input
                  type="text"
                  value={profileData.lastName}
                  onChange={(e) => setProfileData({ ...profileData, lastName: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid var(--border)',
                    fontSize: '14px',
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginBottom: '8px',
                  fontWeight: '500',
                  fontSize: '14px',
                }}>
                  <Mail size={16} />
                  Email
                </label>
                <input
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid var(--border)',
                    fontSize: '14px',
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginBottom: '8px',
                  fontWeight: '500',
                  fontSize: '14px',
                }}>
                  <Phone size={16} />
                  Phone
                </label>
                <input
                  type="tel"
                  value={profileData.phone}
                  onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid var(--border)',
                    fontSize: '14px',
                  }}
                />
              </div>

              <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginBottom: '8px',
                  fontWeight: '500',
                  fontSize: '14px',
                }}>
                  <MapPin size={16} />
                  Shipping Address
                </label>
                <input
                  type="text"
                  value={profileData.location.street}
                  onChange={(e) => setProfileData({
                    ...profileData,
                    location: { ...profileData.location, street: e.target.value }
                  })}
                  placeholder="Street Address"
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid var(--border)',
                    fontSize: '14px',
                    marginBottom: '12px',
                  }}
                />
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr',
                  gap: '12px',
                }}>
                  <input
                    type="text"
                    value={profileData.location.city}
                    onChange={(e) => setProfileData({
                      ...profileData,
                      location: { ...profileData.location, city: e.target.value }
                    })}
                    placeholder="City"
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid var(--border)',
                      fontSize: '14px',
                    }}
                  />
                  <input
                    type="text"
                    value={profileData.location.state}
                    onChange={(e) => setProfileData({
                      ...profileData,
                      location: { ...profileData.location, state: e.target.value }
                    })}
                    placeholder="State"
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid var(--border)',
                      fontSize: '14px',
                    }}
                  />
                  <input
                    type="text"
                    value={profileData.location.zipCode}
                    onChange={(e) => setProfileData({
                      ...profileData,
                      location: { ...profileData.location, zipCode: e.target.value }
                    })}
                    placeholder="ZIP Code"
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid var(--border)',
                      fontSize: '14px',
                    }}
                  />
                </div>
              </div>

              <div style={{ gridColumn: isMobile ? '1' : '1 / -1' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginBottom: '8px',
                  fontWeight: '500',
                  fontSize: '14px',
                }}>
                  <FileText size={16} />
                  Bio
                </label>
                <textarea
                  value={profileData.bio}
                  onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                  rows={4}
                  placeholder="Tell us about yourself..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid var(--border)',
                    fontSize: '14px',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                  }}
                />
              </div>
            </div>

            {/* Save / Cancel */}
            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                type="button"
                onClick={() => { setEditingProfile(false); setProfileImage(null); setMessage({ type: '', text: '' }); }}
                className="btn"
                style={{ padding: '12px 24px', border: '1px solid var(--border)' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  background: 'var(--primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                }}
              >
                <Save size={20} />
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      )}

      {/* Password Tab */}
      {activeTab === 'password' && (
        <form onSubmit={handlePasswordUpdate}>
          <div style={{
            background: 'var(--cardBackground)',
            padding: isMobile ? '20px' : '32px',
            borderRadius: '12px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            maxWidth: '500px',
          }}>
            <h3 style={{ marginBottom: '24px', fontSize: '20px' }}>Change Password</h3>

            {/* Current Password */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                fontSize: '14px',
              }}>
                Current Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    paddingRight: '48px',
                    borderRadius: '8px',
                    border: '1px solid var(--border)',
                    fontSize: '14px',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--textSecondary)',
                  }}
                >
                  {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                fontSize: '14px',
              }}>
                New Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  required
                  minLength={6}
                  style={{
                    width: '100%',
                    padding: '12px',
                    paddingRight: '48px',
                    borderRadius: '8px',
                    border: '1px solid var(--border)',
                    fontSize: '14px',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--textSecondary)',
                  }}
                >
                  {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div style={{ marginBottom: '24px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontWeight: '500',
                fontSize: '14px',
              }}>
                Confirm New Password
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '12px',
                    paddingRight: '48px',
                    borderRadius: '8px',
                    border: '1px solid var(--border)',
                    fontSize: '14px',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--textSecondary)',
                  }}
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Update Button */}
            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px',
                background: 'var(--primary)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
              }}
            >
              {loading ? 'Updating...' : 'Update Password'}
            </button>

            <p style={{
              marginTop: '16px',
              fontSize: '13px',
              color: 'var(--textSecondary)',
              textAlign: 'center',
            }}>
              You will be logged out after changing your password
            </p>
          </div>
        </form>
      )}

      {/* Payment Tab (rendered inline so the tab bar stays) */}
      {activeTab === 'payment' && (
        <PaymentSettings embedded />
      )}

    </div>
  );
};

export default Settings;
